import { Percent, Plus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { DataTable, useDataTable } from '@nucleus-commerce/admin-kit/index-table';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import type { TaxRate } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useStoreDetail } from '@/features/stores';
import { SECTION_ANCHORS } from '@/shared/navigation/section-anchors';
import { ReadOnlyBanner } from '@/shared/store/read-only-banner';
import { useStore } from '@/shared/store/store-context';
import { useStoreEditable } from '@/shared/store/use-store-editable';

import { DeleteTaxRateDialog } from '../components/delete-tax-rate-dialog';
import { useTaxRateColumns } from '../components/tax-rate-columns';
import { TaxRateDialog } from '../components/tax-rate-dialog';
import { taxRateFilters } from '../components/tax-rate-filters';
import { useTaxRateRowActions, type TaxRateAction } from '../components/tax-rate-row-actions';
import { TaxSettingsCard } from '../components/tax-settings-card';
import { useTaxRates } from '../hooks/use-tax-rates';

const EMPTY: readonly TaxRate[] = [];

type Editing = { open: false } | { open: true; rate: TaxRate | null };

function RatesSection({ storeId, countryCode }: { storeId: string; countryCode: string }) {
  const { t } = useTranslation('tax');
  const query = useTaxRates(storeId);
  const [editing, setEditing] = useState<Editing>({ open: false });
  const [deleting, setDeleting] = useState<TaxRate | null>(null);
  const { editable } = useStoreEditable({ permission: 'settings.write' });
  const rates = query.data ?? EMPTY;
  const nextPriority = rates.reduce((max, r) => Math.max(max, r.priority + 1), 0);

  const onAction = useCallback((action: TaxRateAction, rate: TaxRate) => {
    if (action === 'edit') setEditing({ open: true, rate });
    else setDeleting(rate);
  }, []);
  const columns = useTaxRateColumns();
  const filters = useMemo(() => taxRateFilters(t, rates), [rates, t]);
  const rowActions = useTaxRateRowActions(onAction);
  const table = useDataTable({ data: rates, columns, filters, getRowId: (row) => row.id });

  const addButton = editable ? (
    <Button
      size="sm"
      onClick={() => setEditing({ open: true, rate: null })}
      data-tour="settings.tax.add-rate"
    >
      <Plus aria-hidden />
      {t('create.action')}
    </Button>
  ) : null;

  return (
    <FormSection
      title={t('rates.title')}
      description={t('rates.description')}
      id={SECTION_ANCHORS.taxRates}
      tour="settings.tax.rates"
    >
      <div className="flex justify-end">{rates.length > 0 ? addButton : null}</div>
      {query.data && rates.length === 0 ? (
        <EmptyState
          icon={Percent}
          title={t('rates.empty.title')}
          description={t('rates.empty.description')}
          action={addButton}
          tour="settings.tax.empty"
        />
      ) : (
        <DataTable
          table={table}
          search={{ label: t('rates.search'), placeholder: t('rates.search') }}
          rowActions={editable ? rowActions : undefined}
          getRowLabel={(row) => row.name}
          resourceName={{ singular: t('rates.resource_one'), plural: t('rates.resource_other') }}
          loading={query.isPending}
          fetching={query.isFetching}
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      )}
      {editing.open ? (
        <TaxRateDialog
          key={editing.rate?.id ?? 'new'}
          storeId={storeId}
          rate={editing.rate}
          defaultCountry={countryCode}
          nextPriority={nextPriority}
          open
          onOpenChange={(open) => {
            if (!open) setEditing({ open: false });
          }}
        />
      ) : null}
      <DeleteTaxRateDialog
        storeId={storeId}
        rate={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </FormSection>
  );
}

function Tax() {
  const { t } = useTranslation('tax');
  const current = useStore();
  const query = useStoreDetail(current.id);
  const store = query.data;
  const { reason } = useStoreEditable({ permission: 'settings.write' });

  return (
    <PageContent>
      <PageHeader title={t('title')} description={t('description', { store: current.name })} />
      <ReadOnlyBanner reason={reason} />
      {query.isPending ? (
        <Skeleton className="h-48" />
      ) : query.error || !store ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <div className="flex flex-col gap-6">
          <TaxSettingsCard key={store.updatedAt} store={store} />
          <Alert>
            <Percent aria-hidden />
            <AlertDescription>{t('rates.onlyManual')}</AlertDescription>
          </Alert>
          {store.settings.tax.provider === 'manual' ? (
            <RatesSection storeId={store.id} countryCode={store.countryCode} />
          ) : null}
        </div>
      )}
    </PageContent>
  );
}

export function TaxPage() {
  return (
    <FeatureErrorBoundary name="tax">
      <Tax />
    </FeatureErrorBoundary>
  );
}
